import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Col, Form, InputNumber, Modal, Row, Space, Tooltip } from 'antd';
import { useEffect } from 'react';
import { formatFnfAmount, getFnfCurrencySymbol } from './fnfFormatters';
import styles from './index.module.scss';

type Props = {
	isOpen: boolean;
	isLoading: boolean;
	selectedEmployee: any;
	onCancel: () => void;
	onSubmit: (values: {
		leaveDays: number;
		leaveAmount: number;
		gratuityAmount: number;
	}) => void;
};

const formatDate = (value?: string | Date | null) => {
	if (!value) return '-';
	return new Date(value).toLocaleDateString('en-GB');
};

const getRequiredLabel = (label: string) => (
	<span>
		{label}
		<span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
	</span>
);

const WAGEWORKS_CALCULATED_INFO =
	'Reference entitlement amounts calculated by Wageworks from the employee\'s payroll records. Enter the actual settlement values below, or apply these amounts using the link on the right.';

const formatCalculatedDays = (value?: number | null) => {
	if (value === null || value === undefined) return '-';
	return `${value} days`;
};

const ClearFnfModal = ({
	isOpen,
	isLoading,
	selectedEmployee,
	onCancel,
	onSubmit,
}: Props) => {
	const [form] = Form.useForm();

	useEffect(() => {
		if (isOpen && selectedEmployee) {
			form.setFieldsValue({
				leaveDays: selectedEmployee.leaveDays ?? undefined,
				leaveAmount: selectedEmployee.leaveAmount ?? undefined,
				gratuityAmount: selectedEmployee.gratuityAmount ?? undefined,
			});
		} else {
			form.resetFields();
		}
	}, [isOpen, selectedEmployee, form]);

	const applyCalculatedValues = () => {
		if (!selectedEmployee) return;
		form.setFieldsValue({
			leaveDays: selectedEmployee.calculatedLeaveDays ?? 0,
			leaveAmount: selectedEmployee.calculatedLeaveAmount ?? 0,
			gratuityAmount: selectedEmployee.calculatedGratuityAmount ?? 0,
		});
	};

	const hasCalculatedValues =
		selectedEmployee &&
		(selectedEmployee.calculatedLeaveDays != null ||
			selectedEmployee.calculatedLeaveAmount != null ||
			selectedEmployee.calculatedGratuityAmount != null);

	const handleOk = async () => {
		try {
			const values = await form.validateFields();
			onSubmit(values);
		} catch {
			// Ant Design already shows field errors under the inputs
		}
	};

	const currencySymbol = getFnfCurrencySymbol(selectedEmployee?.currency);

	return (
		<Modal
			title="Clear Leave & Gratuity"
			open={isOpen}
			onCancel={onCancel}
			footer={null}
			destroyOnClose
			width={560}
			rootClassName={styles['clear-fn-modal-root']}
		// centered
		>
			{selectedEmployee && (
				<div className={styles['clear-fn-modal__employee-card']}>
					<p className={styles['clear-fn-modal__employee-name']}>
						{selectedEmployee.employeeName}
						<span className={styles['clear-fn-modal__employee-code']}>
							({selectedEmployee.employeeCode})
						</span>
					</p>
					<div className={styles['clear-fn-modal__meta-row']}>
						<div className={styles['clear-fn-modal__meta-item']}>
							<span className={styles['clear-fn-modal__meta-label']}>
								Relieving Date
							</span>
							<span className={styles['clear-fn-modal__meta-value']}>
								{formatDate(selectedEmployee.relievingDate)}
							</span>
						</div>
						<div className={styles['clear-fn-modal__meta-item']}>
							<span className={styles['clear-fn-modal__meta-label']}>
								Last Pay Period
							</span>
							<span className={styles['clear-fn-modal__meta-value']}>
								{selectedEmployee.lastPayPeriod || '-'}
							</span>
						</div>
						<div className={styles['clear-fn-modal__meta-item']}>
							<span className={styles['clear-fn-modal__meta-label']}>
								Currency
							</span>
							<span className={styles['clear-fn-modal__meta-value']}>
								{selectedEmployee.currency ?? '-'}
							</span>
						</div>
					</div>
				</div>
			)}

			<div className={styles['clear-fn-modal__reference']}>
				<div className={styles['clear-fn-modal__reference-header']}>
					<p className={styles['clear-fn-modal__reference-title']}>
						Wageworks Calculated
						<Tooltip title={WAGEWORKS_CALCULATED_INFO}>
							<InfoCircleOutlined
								className={styles['clear-fn-modal__reference-info']}
							/>
						</Tooltip>
					</p>
					{hasCalculatedValues && (
						<Button
							type="link"
							size="small"
							className={styles['clear-fn-modal__reference-action']}
							onClick={applyCalculatedValues}
							disabled={isLoading}
						>
							Use these values
						</Button>
					)}
				</div>
				<div className={styles['clear-fn-modal__reference-row']}>
					<div className={styles['clear-fn-modal__reference-item']}>
						<span className={styles['clear-fn-modal__meta-label']}>
							Leave Days
						</span>
						<span className={styles['clear-fn-modal__meta-value']}>
							{formatCalculatedDays(
								selectedEmployee?.calculatedLeaveDays,
							)}
						</span>
					</div>
					<div className={styles['clear-fn-modal__reference-item']}>
						<span className={styles['clear-fn-modal__meta-label']}>
							Leave Pay
						</span>
						<span className={styles['clear-fn-modal__meta-value']}>
							{formatFnfAmount(
								selectedEmployee?.calculatedLeaveAmount,
								selectedEmployee?.currency,
							)}
						</span>
					</div>
					<div className={styles['clear-fn-modal__reference-item']}>
						<span className={styles['clear-fn-modal__meta-label']}>
							Gratuity Pay
						</span>
						<span className={styles['clear-fn-modal__meta-value']}>
							{formatFnfAmount(
								selectedEmployee?.calculatedGratuityAmount,
								selectedEmployee?.currency,
							)}
						</span>
					</div>
				</div>
			</div>

			<p className={styles['clear-fn-modal__section-title']}>
				Settlement Details
			</p>

			<Form
				form={form}
				layout="vertical"
				requiredMark={false}
				className={styles['clear-fn-modal__form']}
			>
				<Row gutter={16}>
					<Col span={6}>
						<Form.Item
							label={getRequiredLabel('Leave Days')}
							name="leaveDays"
							rules={[
								{ required: true, message: 'Required' },
								{
									type: 'number',
									min: 0,
									message: 'Leave days must be 0 or greater',
								},
							]}
						>
							<InputNumber
								min={0}
								precision={0}
								size="large"
								placeholder="Days"
								style={{ width: '100%' }}
							/>
						</Form.Item>
					</Col>
					<Col span={9}>
						<Form.Item
							label={getRequiredLabel('Leave Pay')}
							name="leaveAmount"
							rules={[
								{ required: true, message: 'Required' },
								{
									type: 'number',
									min: 0,
									message: 'Leave pay must be 0 or greater',
								},
							]}
						>
							<InputNumber
								min={0}
								precision={2}
								controls={false}
								size="large"
								placeholder="0.00"
								{...(currencySymbol
									? { addonBefore: currencySymbol }
									: {})}
								style={{ width: '100%' }}
							/>
						</Form.Item>
					</Col>
					<Col span={9}>
						<Form.Item
							label={getRequiredLabel('Gratuity Pay')}
							name="gratuityAmount"
							rules={[
								{
									required: true,
									message: 'Required',
								},
								{
									type: 'number',
									min: 0,
									message: 'Gratuity pay must be 0 or greater',
								},
							]}
						>
							<InputNumber
								min={0}
								precision={2}
								controls={false}
								size="large"
								placeholder="0.00"
								{...(currencySymbol
									? { addonBefore: currencySymbol }
									: {})}
								style={{ width: '100%' }}
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<div className={styles['clear-fn-modal__footer']}>
				<Space>
					<Button onClick={onCancel} disabled={isLoading}>
						Cancel
					</Button>
					<Button
						type="primary"
						loading={isLoading}
						onClick={handleOk}
					>
						Submit
					</Button>
				</Space>
			</div>
		</Modal>
	);
};

export default ClearFnfModal;
